import { useMemo, useState } from 'react';
import { Card, Calendar, Badge, List, Typography, Empty, Tag } from 'antd';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import useScheduledHangouts from '../../hooks/use_scheduled_hangouts';
import './HangoutCalendar.css';

const { Title, Text } = Typography;

/**
 * Month calendar of every scheduled hangout the user can see, across all
 * their entities — behind feature_hangout_schedule. Each day cell badges its
 * sessions; selecting a day lists them with a jump to the owning entity.
 */
function HangoutCalendar() {
    const { items, loading } = useScheduledHangouts();
    const navigate = useNavigate();
    const [selectedDate, setSelectedDate] = useState(dayjs());

    const byDate = useMemo(() => {
        const map = {};
        for (const item of items) {
            const key = dayjs(item.scheduled_at).format('YYYY-MM-DD');
            (map[key] = map[key] || []).push(item);
        }
        for (const key of Object.keys(map)) {
            map[key].sort((a, b) => dayjs(a.scheduled_at).valueOf() - dayjs(b.scheduled_at).valueOf());
        }
        return map;
    }, [items]);

    const cellRender = (current, info) => {
        if (info.type !== 'date') return info.originNode;
        const dayItems = byDate[current.format('YYYY-MM-DD')] || [];
        if (!dayItems.length) return info.originNode;
        return (
            <ul className="hangout-calendar-cell">
                {dayItems.slice(0, 3).map(item => (
                    <li key={item.id}>
                        <Badge status="processing" text={item.title || item.entity_name} />
                    </li>
                ))}
                {dayItems.length > 3 && (
                    <li><Text type="secondary" style={{ fontSize: 12 }}>+{dayItems.length - 3} more</Text></li>
                )}
            </ul>
        );
    };

    const selectedItems = byDate[selectedDate.format('YYYY-MM-DD')] || [];

    return (
        <Card loading={loading} style={{ maxWidth: 900, margin: '0 auto' }}>
            <Title level={4} style={{ marginTop: 0 }}>Hangout schedule</Title>
            <Text type="secondary">Every upcoming (and past) hangout across your contexts and relationships.</Text>
            <Calendar cellRender={cellRender} onSelect={setSelectedDate} style={{ marginTop: 16 }} />
            <div style={{ marginTop: 8 }}>
                <Title level={5}>{selectedDate.format('D MMMM YYYY')}</Title>
                {selectedItems.length === 0 ? (
                    <Empty description="No hangouts scheduled" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                    <List
                        dataSource={selectedItems}
                        renderItem={item => (
                            <List.Item
                                actions={[
                                    <Typography.Link key="open" onClick={() => navigate(`/entity/${item.entity_id}`)}>Open</Typography.Link>,
                                ]}
                            >
                                <List.Item.Meta
                                    title={item.kind === 'occasion' ? <>{item.title} <Tag color="blue">occasion</Tag></> : (item.title || 'Hangout')}
                                    description={`${item.entity_name} · ${dayjs(item.scheduled_at).format('HH:mm')}`}
                                />
                            </List.Item>
                        )}
                    />
                )}
            </div>
        </Card>
    );
}

export default HangoutCalendar;
